import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ExtendedUser } from '../../Types';
import { ViewContext } from './types';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column/column';
import Row from '@amzn/meridian/row';
import SideMenu from '@amzn/meridian/side-menu/side-menu';
import SideMenuLink from '@amzn/meridian/side-menu/side-menu-link';
import Text from '@amzn/meridian/text';
import UserBadge from '../Dashboard/DashboardComponents/UserBadge';
import { SummariesErrorAlert } from './common/Alerts';
import CSASummariesContent from './CSASummariesContent';
import CenteredLoader from '../../commons/CenteredLoader';
import {
  ClickstreamTypes,
  useClickstreamTags,
} from '../../hooks/useClickstreamTags';
import { CSA_SUMMARIES, CSA_SUMMARIES_SLOT } from './constants';
import { getTeamWeeklySummaries } from '../../api/Summaries/query';
import { useQuery } from '@apollo/client';
import useTmAlias from './hooks/useTmAlias';
import useSummaryTargetLanguage from './hooks/useSummaryTargetLanguage';

interface CSASummariesTMViewProps {
  directReports: ExtendedUser[];
  viewContext: ViewContext;
}

export const CSASummariesTMView = ({
  directReports,
  viewContext,
}: CSASummariesTMViewProps) => {
  const tmAlias = useTmAlias();

  const [selectedDirectReport, setSelectedDirectReport] =
    useState<ExtendedUser>(directReports[0]); // we know there is at least 1 direct report here as this component is gated by isProfileTm in CSASummariesContainer

  const directReportsAliases = useMemo(
    () => directReports.map(reportee => reportee.alias),
    [directReports]
  );

  const targetLanguage = useSummaryTargetLanguage();
  const { data, loading, error, refetch } = useQuery(getTeamWeeklySummaries, {
    fetchPolicy: 'cache-first',
    variables: {
      aliases: directReportsAliases,
      managerAlias: tmAlias,
      targetLanguage,
    },
    skip: tmAlias === '',
  });
  const weeklySummaries = data?.getTeamLatestSummaries.results ?? [];

  const selectedSummary = useMemo(() => {
    return weeklySummaries.find(
      summary => summary.alias === selectedDirectReport.alias
    );
  }, [weeklySummaries, selectedDirectReport]);

  if (loading) {
    return <CenteredLoader data-testid='tm-view-loader' />;
  }

  if (error || data?.getTeamLatestSummaries.error) {
    return <SummariesErrorAlert data-testid='tm-view-error' />;
  }

  return (
    <Row
      widths={['fit', 'fill']}
      spacingInset='300 none none none'
      alignmentVertical='stretch'
      height='450px'
      data-testid='tm-view-container'
    >
      <Column
        overflowY='scroll'
        spacing='none'
        spacingInset='none none none none'
      >
        <DirectReportList
          directReports={directReports}
          selectedDirectReport={selectedDirectReport}
          setSelectedDirectReport={setSelectedDirectReport}
        />
      </Column>
      <Column overflowY='auto' spacing='400'>
        <Box spacingInset='400 none none none'>
          <Text type='h300' data-testid='tm-view-header'>
            {selectedDirectReport.displayName}
          </Text>
        </Box>
        <CSASummariesContent
          key={selectedDirectReport.alias}
          spacingInset='none none none none'
          displayedCsa={selectedDirectReport}
          viewContext={viewContext}
          refetch={refetch}
          initialSummary={selectedSummary?.summary ?? null}
        />
      </Column>
    </Row>
  );
};

interface DirectReportListProps {
  directReports: ExtendedUser[];
  selectedDirectReport?: ExtendedUser;
  setSelectedDirectReport: (reportee: ExtendedUser) => void;
}
const DirectReportList = ({
  directReports,
  selectedDirectReport,
  setSelectedDirectReport,
}: DirectReportListProps) => {
  const selectDirectReportClickstream = useClickstreamTags({
    type: ClickstreamTypes.Link,
    slotId: CSA_SUMMARIES_SLOT,
    feature: CSA_SUMMARIES,
    action: 'Select Direct Report',
  });

  return (
    <SideMenu titleComponent={Text} data-testid='direct-report-list'>
      <Text type='h200'>
        <FormattedMessage
          id='cs_perfportal_associates_with_count'
          defaultMessage='Associates ({count})'
          values={{ count: directReports.length }}
        />
      </Text>
      {directReports.map(reportee => {
        const isSelected = selectedDirectReport?.alias === reportee.alias;
        return (
          <SideMenuLink
            selected={isSelected}
            data-testid={`direct-report-${reportee.alias}${
              isSelected ? '-selected' : ''
            }`}
            onClick={() => {
              setSelectedDirectReport(reportee);
            }}
            key={reportee.alias}
            {...selectDirectReportClickstream}
            data-csa-c-tag4={reportee.alias}
          >
            <Row
              maxWidth='200px'
              spacingInset='none none none none'
              spacing='300'
            >
              <UserBadge
                username={reportee.alias}
                thumbNail
                thumbnailSize='small'
              />
              <Text truncate>{reportee.displayName}</Text>
            </Row>
          </SideMenuLink>
        );
      })}
    </SideMenu>
  );
};

export default CSASummariesTMView;
